import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Portal, usePopupManager } from "@/components/Shared";
import { usePrefersReducedMotion } from "@/hooks";
import { hasConsentBeenShown, trackEvent } from "@/lib/analytics";
import ChatLauncher from "./ChatLauncher";
import ChatTeaser from "./ChatTeaser";
import { CHAT_POPUP_ID, COPY, EXCLUDED_ROUTES, KICK_POPUP_ID, TEASER_SESSION_KEY, type Language } from "./copy";
import { useChatSession } from "./useChatSession";

/**
 * "Message Jorge" — the site's chat widget. Design write-up: docs/forms/chat-widget.md.
 *
 * What lives here is the gating and the chrome: when the launcher and teaser may show, how
 * they share the corner with the timed Kick popup and the cookie banner, and the lazy load of
 * the panel. The conversation itself is useChatSession; the dialog is ChatPanel.
 *
 * Ships dark: renders nothing unless CHAT_WIDGET_ENABLED=true at build time, so the site can
 * deploy before the /chat Lambda route exists.
 */

const ChatPanel = lazy(() => import(/* webpackChunkName: "chat-panel" */ "./ChatPanel"));
const preloadPanel = () => {
  void import(/* webpackChunkName: "chat-panel" */ "./ChatPanel");
};

const TEASER_DELAY_MS = 8_000;
const TEASER_SCROLL_FRACTION = 0.35;
const TEASER_AUTO_HIDE_MS = 25_000;
const CONSENT_POLL_MS = 1_000;

type TeaserState = "idle" | "visible" | "done";

function teaserAlreadyShown(): boolean {
  try {
    return sessionStorage.getItem(TEASER_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

interface ChatWidgetProps {
  language: Language;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ language }) => {
  const enabled = process.env.CHAT_WIDGET_ENABLED === "true";
  const { pathname } = useLocation();
  const copy = COPY[language];
  const reducedMotion = usePrefersReducedMotion();
  const { request, dismiss, activeId } = usePopupManager();
  const { state, actions } = useChatSession(language, pathname);
  const launcherRef = useRef<HTMLButtonElement>(null);

  // The cookie banner owns the bottom of the screen until it is answered.
  const [consentShown, setConsentShown] = useState<boolean>(() => typeof window !== "undefined" && hasConsentBeenShown());
  useEffect(() => {
    if (consentShown) return;
    const timer = setInterval(() => {
      if (hasConsentBeenShown()) setConsentShown(true);
    }, CONSENT_POLL_MS);
    return () => clearInterval(timer);
  }, [consentShown]);

  const [teaser, setTeaser] = useState<TeaserState>(() => (teaserAlreadyShown() ? "done" : "idle"));

  const excluded = EXCLUDED_ROUTES.some((route) => pathname === route || pathname === `${route}/`);
  const otherPopupActive = activeId !== null && activeId !== CHAT_POPUP_ID;
  const showChrome = enabled && !excluded && consentShown && !otherPopupActive;

  // Hold the popup slot while open so the timed Kick promo queues behind the conversation
  // instead of landing on top of it. If the promo is already up, it steps aside.
  useEffect(() => {
    if (!state.open) return;
    if (activeId === KICK_POPUP_ID) dismiss(KICK_POPUP_ID);
    return request({ id: CHAT_POPUP_ID, priority: "high", render: () => null });
    // `activeId` is read once, at open time, on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, request, dismiss]);

  // The teaser: after a pause or a third of the page, once per session, never over an open panel.
  useEffect(() => {
    if (!showChrome || teaser !== "idle" || state.open) return;
    const show = () => {
      setTeaser("visible");
      try {
        sessionStorage.setItem(TEASER_SESSION_KEY, "1");
      } catch {
        // storage blocked — the teaser simply may show again next page load
      }
      trackEvent("chat_teaser_shown", { page_language: language });
    };
    const timer = setTimeout(show, TEASER_DELAY_MS);
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= TEASER_SCROLL_FRACTION) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [showChrome, teaser, state.open, language]);

  useEffect(() => {
    if (teaser !== "visible") return;
    const timer = setTimeout(() => setTeaser("done"), TEASER_AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [teaser]);

  const openFrom = useCallback(
    (source: "launcher" | "teaser") => {
      setTeaser("done");
      actions.open(source);
    },
    [actions],
  );
  const dismissTeaser = useCallback(() => setTeaser("done"), []);

  if (!enabled) return null;

  return (
    <Portal id="chat-widget-portal">
      {showChrome && (
        <ChatLauncher ref={launcherRef} copy={copy} open={state.open} onOpen={() => openFrom("launcher")} onPreload={preloadPanel} />
      )}
      <AnimatePresence>
        {showChrome && teaser === "visible" && !state.open && (
          <ChatTeaser key="chat-teaser" copy={copy} reducedMotion={reducedMotion} onOpen={() => openFrom("teaser")} onDismiss={dismissTeaser} />
        )}
      </AnimatePresence>
      {state.open && (
        <Suspense fallback={null}>
          <ChatPanel copy={copy} state={state} actions={actions} launcherRef={launcherRef} reducedMotion={reducedMotion} />
        </Suspense>
      )}
    </Portal>
  );
};

export default ChatWidget;
